import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import { COLORS } from "../../constants";
import {
  mobileAppAdminService,
  type MobileAppUserVersionRow,
} from "../../services/mobileAppAdminService";

const qkCurrent = ["mobile-app", "current"] as const;
const qkUsersBase = ["mobile-app", "user-versions"] as const;

const APP_ACTIVE_MS = 15 * 60 * 1000;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

function isRecentAppContact(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < APP_ACTIVE_MS;
}

const AtualizarApk: React.FC = () => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const [filterDraft, setFilterDraft] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const currentQuery = useQuery({
    queryKey: qkCurrent,
    queryFn: () => mobileAppAdminService.getCurrentRelease(),
  });

  const usersQuery = useQuery({
    queryKey: [...qkUsersBase, currentPage, appliedFilter],
    queryFn: () =>
      mobileAppAdminService.getUserVersions({
        page: currentPage,
        limit: itemsPerPage,
        q: appliedFilter || undefined,
      }),
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Selecione o arquivo .apk");
      return mobileAppAdminService.publishApk({ file });
    },
    onSuccess: async ({ release, versionWarning }) => {
      toast.success(
        `APK publicado (versionCode ${release.versionCode}, versionName ${release.versionName}). ` +
          "O servidor leu esses valores do manifest do arquivo.",
      );
      if (versionWarning) {
        toast.warn(versionWarning, { autoClose: 12000 });
      }
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: qkCurrent });
      await queryClient.invalidateQueries({ queryKey: qkUsersBase });
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao publicar o APK.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Selecione o arquivo .apk");
      return mobileAppAdminService.restoreCurrentApk({ file });
    },
    onSuccess: async (release) => {
      toast.success(
        `APK restaurado no servidor (${release.versionName}, build ${release.versionCode}). ` +
          "Os aparelhos já podem baixar novamente.",
      );
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: qkCurrent });
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao restaurar o APK.");
    },
  });

  const downloadMutation = useMutation({
    mutationFn: () => mobileAppAdminService.downloadCurrentApk(),
    onSuccess: () => {
      toast.success("Download do APK iniciado.");
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao baixar o APK.");
    },
  });

  const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f && f.name.toLowerCase().endsWith(".apk") ? f : null);
    if (f && !f.name.toLowerCase().endsWith(".apk")) {
      toast.warn("Selecione um arquivo com extensão .apk");
    }
  }, []);

  const handleSearch = useCallback(() => {
    setAppliedFilter(filterDraft.trim());
    setCurrentPage(1);
  }, [filterDraft]);

  const handleClearFilter = useCallback(() => {
    setFilterDraft("");
    setAppliedFilter("");
    setCurrentPage(1);
  }, []);

  const userRows = usersQuery.data?.usuarios ?? [];
  const totalItems = usersQuery.data?.total ?? 0;
  const totalPages = usersQuery.data?.totalPages ?? 0;

  const activeFiltersCount = useMemo(
    () => (appliedFilter ? 1 : 0),
    [appliedFilter]
  );

  return (
    <main className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Atualizar APK (app)"
        description="Envie o .apk gerado no repositório do app (preferencialmente com python3 gerar_apk.py, que sobe versionCode e semver). O servidor lê versionCode e versionName do manifest. Se o build não for mais novo que a versão ativa, a publicação ainda é aceita — um aviso é exibido."
      />

      <section className="mb-10 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-neutral-900">
          Publicar nova versão
        </h2>
        <p className="mb-4 max-w-2xl text-sm text-neutral-600">
          Não é necessário informar números de versão manualmente: o backend
          extrai do próprio APK. Recomenda-se gerar com{" "}
          <code className="rounded bg-neutral-100 px-1">
            python3 gerar_apk.py
          </code>{" "}
          (bump + build). Publicar um versionCode menor ou igual ao atual é
          permitido, mas os aparelhos podem não pedir atualização.
        </p>
        <div className="grid max-w-xl gap-4">
          <label className="block text-sm font-medium text-neutral-700">
            Arquivo (.apk)
            <input
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              className="mt-1 block w-full text-sm text-neutral-800"
              onChange={onPickFile}
            />
          </label>
          <button
            type="button"
            disabled={publishMutation.isPending || !file}
            onClick={() => {
              const current = currentQuery.data;
              const msg = current
                ? `Versão ativa no servidor: ${current.versionName} (build ${current.versionCode}).\n\n` +
                  `Se o APK enviado não tiver versionCode maior, a publicação será aceita mesmo assim, ` +
                  `mas os aparelhos podem não receber o alerta de atualização.\n\nContinuar?`
                : "Publicar este APK como primeira versão no servidor?";
              if (!window.confirm(msg)) return;
              publishMutation.mutate();
            }}
            className="w-fit rounded-lg px-4 py-2 text-sm font-semibold text-white shadow transition disabled:opacity-60"
            style={{ backgroundColor: COLORS.PRIMARY }}
          >
            {publishMutation.isPending ? "Enviando…" : "Publicar APK"}
          </button>
        </div>
      </section>

      <section className="mb-10 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <h2 className="mb-2 text-lg font-semibold text-neutral-900">
          Versão ativa no servidor
        </h2>
        {currentQuery.isLoading ? (
          <p className="text-neutral-600">Carregando…</p>
        ) : currentQuery.isError ? (
          <p className="text-red-600">
            Não foi possível carregar a versão atual.
          </p>
        ) : !currentQuery.data ? (
          <p className="text-neutral-600">
            Nenhum APK publicado ainda. Envie o primeiro acima.
          </p>
        ) : (
          <>
            {currentQuery.data.fileOnDisk === false && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
                <p className="font-semibold">Arquivo ausente no servidor</p>
                <p className="mt-1">
                  A versão {currentQuery.data.versionName} (build{" "}
                  {currentQuery.data.versionCode}) está registrada no banco, mas
                  o .apk não existe em disco — o app recebe erro 404 ao baixar.
                  Reenvie abaixo o mesmo build (mesmo versionCode) ou publique
                  uma versão nova.
                </p>
                <button
                  type="button"
                  disabled={restoreMutation.isPending || !file}
                  onClick={() => restoreMutation.mutate()}
                  className="mt-3 rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {restoreMutation.isPending
                    ? "Restaurando…"
                    : "Restaurar APK da versão ativa"}
                </button>
              </div>
            )}
            <ul className="list-inside list-disc text-sm text-neutral-800">
            <li>
              <span className="font-medium">versionCode:</span>{" "}
              {currentQuery.data.versionCode}
            </li>
            <li>
              <span className="font-medium">versionName:</span>{" "}
              {currentQuery.data.versionName}
            </li>
            <li>
              <span className="font-medium">Tamanho:</span>{" "}
              {formatBytes(currentQuery.data.fileSizeBytes)}
            </li>
            <li>
              <span className="font-medium">SHA-256:</span>{" "}
              <code className="break-all text-xs">
                {currentQuery.data.sha256}
              </code>
            </li>
            <li>
              <span className="font-medium">Publicado em:</span>{" "}
              {formatDate(currentQuery.data.createdAt)}
            </li>
            <li>
              <span className="font-medium">Arquivo no disco:</span>{" "}
              {currentQuery.data.fileOnDisk === false ? (
                <span className="font-semibold text-red-700">Não</span>
              ) : (
                <span className="font-semibold text-emerald-700">Sim</span>
              )}
            </li>
          </ul>
            {currentQuery.data.fileOnDisk !== false ? (
              <button
                type="button"
                disabled={downloadMutation.isPending}
                onClick={() => downloadMutation.mutate()}
                className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow transition disabled:opacity-60"
                style={{ backgroundColor: COLORS.PRIMARY }}
              >
                {downloadMutation.isPending
                  ? "Baixando…"
                  : `Baixar APK (${currentQuery.data.versionName}, build ${currentQuery.data.versionCode})`}
              </button>
            ) : null}
          </>
        )}
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-neutral-200 p-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Versão reportada pelo app (por usuário)
            </h2>
            <p className="mt-1 text-sm text-neutral-600">
              O app envia heartbeat a cada poucos minutos com rede. A coluna
              &quot;Recente no app&quot; indica contato nos últimos 15 minutos
              (útil para suporte).
            </p>
          </div>
          <div className="flex w-full max-w-md flex-col gap-2 sm:w-auto">
            <label className="text-xs font-medium text-neutral-600">
              Filtrar (nome, e-mail ou loja)
              {activeFiltersCount > 0 ? (
                <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                  filtro ativo
                </span>
              ) : null}
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={filterDraft}
                onChange={(e) => setFilterDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                placeholder="Digite e busque…"
                className="min-w-[180px] flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="rounded-md px-4 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: COLORS.PRIMARY }}
              >
                Buscar
              </button>
              <button
                type="button"
                onClick={handleClearFilter}
                className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                Limpar
              </button>
            </div>
          </div>
        </div>

        {usersQuery.isLoading ? (
          <p className="p-6 text-neutral-600">Carregando…</p>
        ) : usersQuery.isError ? (
          <p className="p-6 text-red-600">Não foi possível carregar a lista.</p>
        ) : (
          <>
            <div className="overflow-x-auto px-2 pb-2">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-2 font-semibold">Usuário</th>
                    <th className="px-3 py-2 font-semibold">Loja</th>
                    <th className="px-3 py-2 font-semibold">versionCode</th>
                    <th className="px-3 py-2 font-semibold">versionName</th>
                    <th className="px-3 py-2 font-semibold">Reportado em</th>
                    <th className="px-3 py-2 font-semibold">Recente no app</th>
                  </tr>
                </thead>
                <tbody>
                  {userRows.map((u: MobileAppUserVersionRow) => {
                    const active = isRecentAppContact(
                      u.mobileAppVersionReportedAt
                    );
                    return (
                      <tr
                        key={u.usuarioId}
                        className="border-b border-neutral-100"
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium text-neutral-900">
                            {u.nome}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {u.email}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-neutral-800">
                          {u.lojaNome ?? "—"}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {u.mobileAppVersionCode ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          {u.mobileAppVersionName ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-600">
                          {formatDate(u.mobileAppVersionReportedAt)}
                        </td>
                        <td className="px-3 py-2">
                          {active ? (
                            <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                              Sim
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-600">
                              Não
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalItems > 0 && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default AtualizarApk;
